/* global BigInt */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { CONTRACTS, DURATIONS } from '../config/contracts';
import {
  formatUSDC,
  formatAPY,
  getOpportunityStatus,
  generateOptionLabels,
  mapOpportunity
} from '../utils/helpers';
import StatusBadge from '../components/StatusBadge';
import './MyBets.css';

function MyBets() {
  const { address, marketContract, positionTokenContract, provider } = useWeb3();
  const navigate = useNavigate();

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBlock, setCurrentBlock] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const loadUserBets = useCallback(async () => {
    if (!marketContract || !positionTokenContract || !address) {
      setLoading(false);
      return;
    }

    try {
      const count = await marketContract.getOpportunityCount();
      if (count === 0n) {
        setPositions([]);
        setLoading(false);
        return;
      }

      // Get current block
      if (provider) {
        const block = await provider.getBlockNumber();
        setCurrentBlock(block);
      }

      const opportunities = await marketContract.getOpportunitiesRange(0, count);

      // Build all token IDs for batch balance query
      // tokenId = opportunityId * 10 + optionIndex
      const validOppIndices = [];
      const allTokenIds = [];
      const allAddresses = [];
      for (let i = 0; i < opportunities.length; i++) {
        const opp = opportunities[i];
        if (!opp.name) continue;
        validOppIndices.push(i);
        for (let opt = 0; opt < 6; opt++) {
          allTokenIds.push(BigInt(i) * 10n + BigInt(opt));
          allAddresses.push(address);
        }
      }

      // Single batched call instead of 6*N calls
      const allBalances = allTokenIds.length > 0
        ? await positionTokenContract.balanceOfBatch(allAddresses, allTokenIds)
        : [];

      // Parse results and filter to positions with stakes
      const userPositions = [];
      for (let idx = 0; idx < validOppIndices.length; idx++) {
        const i = validOppIndices[idx];
        const opp = opportunities[i];

        // Extract this opportunity's 6 balances from the flat array
        const userStakes = allBalances.slice(idx * 6, (idx + 1) * 6);
        const totalUserStake = userStakes.reduce((sum, b) => sum + b, 0n);

        if (totalUserStake > 0n) {
          // Only fetch pool stakes for opportunities user has bets on
          const oppStakes = await marketContract.getTotalStakePerOption(i);
          userPositions.push({
            ...mapOpportunity(opp, i),
            userStakes: Array.from(userStakes),
            totalStakes: oppStakes,
            totalUserStake
          });
        }
      }

      setPositions(userPositions);
      setLoading(false);
    } catch (error) {
      console.error('Error loading bets:', error);
      setLoading(false);
    }
  }, [marketContract, positionTokenContract, address, provider]);

  useEffect(() => {
    loadUserBets();
    const interval = setInterval(loadUserBets, 15000);
    return () => clearInterval(interval);
  }, [loadUserBets]);

  const claimPayout = async (oppId) => {
    if (!positionTokenContract || !address) return;

    setClaiming(oppId);
    try {
      const opp = await marketContract.getOpportunity(oppId);

      // Collect tokens to transfer
      const tokenIds = [];
      const amounts = [];

      if (opp.voided) {
        // Voided: transfer all options user has
        for (let i = 0; i < 6; i++) {
          const tokenId = await marketContract.getTokenId(oppId, i);
          const balance = await positionTokenContract.balanceOf(address, tokenId);
          if (balance > 0n) {
            tokenIds.push(tokenId);
            amounts.push(balance);
          }
        }
      } else {
        // Not voided: transfer only winning option
        const winningOption = opp.winningOption;
        const tokenId = await marketContract.getTokenId(oppId, winningOption);
        const balance = await positionTokenContract.balanceOf(address, tokenId);
        if (balance > 0n) {
          tokenIds.push(tokenId);
          amounts.push(balance);
        }
      }

      if (tokenIds.length === 0) {
        throw new Error('No tokens to claim');
      }

      // Transfer tokens to market contract (triggers claim via hook)
      let tx;
      if (tokenIds.length === 1) {
        tx = await positionTokenContract.safeTransferFrom(
          address,
          CONTRACTS.MARKET_ADDRESS,
          tokenIds[0],
          amounts[0],
          '0x'
        );
      } else {
        tx = await positionTokenContract.safeBatchTransferFrom(
          address,
          CONTRACTS.MARKET_ADDRESS,
          tokenIds,
          amounts,
          '0x'
        );
      }

      await tx.wait();
      setTimeout(loadUserBets, 2000);
    } catch (error) {
      console.error('Error claiming:', error);
      alert('Claim failed: ' + error.message);
    } finally {
      setClaiming(null);
    }
  };

  if (!address) {
    return (
      <div className="connect-prompt">
        <h2>Connect Your Wallet</h2>
        <p>Please connect your wallet to view your bets and positions.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading your bets...</div>;
  }

  // Calculate summary statistics
  const totalStaked = positions.reduce((sum, pos) => sum + pos.totalUserStake, 0n);
  const activePositions = positions.filter(pos => getOpportunityStatus(pos, currentBlock) !== 'resolved').length;
  const resolvedPositions = positions.filter(pos => getOpportunityStatus(pos, currentBlock) === 'resolved');

  let totalWon = 0n;
  resolvedPositions.forEach(pos => {
    if (pos.resolved && !pos.voided && pos.userStakes[Number(pos.winningOption)] > 0n) {
      const userStake = pos.userStakes[Number(pos.winningOption)];
      const payout = (userStake * pos.rewardPerToken) / BigInt(1e18);
      totalWon += payout;
    }
  });

  return (
    <div className="my-bets-page">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-label">Total Staked</div>
          <div className="summary-value">${formatUSDC(totalStaked)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Active Positions</div>
          <div className="summary-value">{activePositions}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Won</div>
          <div className="summary-value positive">${formatUSDC(totalWon)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Positions</div>
          <div className="summary-value">{positions.length}</div>
        </div>
      </div>

      <div className="positions">
        {positions.length === 0 ? (
          <div className="empty">
            You don't have any bets yet. Visit the Markets page to get started!
          </div>
        ) : (
          positions.map(pos => (
            <PositionCard
              key={pos.id}
              position={pos}
              currentBlock={currentBlock}
              claiming={claiming}
              onClaim={() => claimPayout(pos.id)}
              onClick={() => navigate(`/opportunity/${pos.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PositionCard({ position: pos, currentBlock, claiming, onClaim, onClick }) {
  const status = getOpportunityStatus(pos, currentBlock);
  const totalVolume = pos.totalStakes.reduce((sum, stake) => sum + stake, 0n);

  let statusText = '';
  let statusClass = '';
  let canClaim = false;

  if (status === 'resolved') {
    if (pos.voided) {
      statusText = 'VOIDED - REFUND';
      statusClass = 'status-resolved';
      canClaim = true;
    } else {
      statusText = 'RESOLVED';
      statusClass = 'status-resolved';
      canClaim = pos.userStakes[Number(pos.winningOption)] > 0n;
    }
  } else if (status === 'monitoring') {
    statusText = 'MONITORING';
    statusClass = 'status-monitoring';
  } else {
    statusText = 'BETTING OPEN';
    statusClass = 'status-betting';
  }

  const handleClaimClick = (e) => {
    e.stopPropagation();
    onClaim();
  };

  return (
    <div className="position-card" onClick={onClick}>
      <div className="position-header">
        <div className="position-title">
          <h3>{pos.name}</h3>
          <span className="duration-badge">
            {DURATIONS[Number(pos.durationEnum)]?.shortLabel || '?'}
          </span>
        </div>
        <span className={`status-badge ${statusClass}`}>{statusText}</span>
      </div>

      <div className="position-stats">
        <div className="stat">
          <div className="stat-label">Your Stake</div>
          <div className="stat-value">${formatUSDC(pos.totalUserStake)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Betting With Respect To</div>
          <div className="stat-value">{formatAPY(pos.startApy)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Total Volume</div>
          <div className="stat-value">${formatUSDC(totalVolume)}</div>
        </div>
      </div>

      <div className="bets-grid">
        {pos.userStakes.slice(0, 3).map((stake, index) => {
          const optionLabels = generateOptionLabels(pos.thresholds, pos.startApy);
          const option = optionLabels[index];
          const hasBet = stake > 0n;
          const isWinner = status === 'resolved' && !pos.voided && Number(pos.winningOption) === index;
          const isLoser = status === 'resolved' && !pos.voided && hasBet && Number(pos.winningOption) !== index;
          const isClaimed = isWinner && !hasBet;

          return (
            <div
              key={index}
              className={`bet-card ${option?.type || 'negative'} ${hasBet ? 'has-bet' : ''} ${isWinner ? 'winner' : ''} ${isLoser ? 'loser' : ''} ${isClaimed ? 'claimed' : ''}`}
              title={option?.tooltip || ''}
            >
              <div className="option-main">{option?.main || `${index}`}</div>
              <div className="option-sub">{option?.sub || ''}</div>
              {hasBet && <div className="bet-amount">${formatUSDC(stake)}</div>}
              {isClaimed && <div className="claimed-badge">CLAIMED</div>}
            </div>
          );
        })}

        <div className="benchmark-card-small">
          <div className="benchmark-apy-small">{formatAPY(pos.startApy)}</div>
          <div className="benchmark-label-small">APY</div>
        </div>

        {pos.userStakes.slice(3, 6).map((stake, index) => {
          const actualIndex = index + 3;
          const optionLabels = generateOptionLabels(pos.thresholds, pos.startApy);
          const option = optionLabels[actualIndex];
          const hasBet = stake > 0n;
          const isWinner = status === 'resolved' && !pos.voided && Number(pos.winningOption) === actualIndex;
          const isLoser = status === 'resolved' && !pos.voided && hasBet && Number(pos.winningOption) !== actualIndex;
          const isClaimed = isWinner && !hasBet;

          return (
            <div
              key={actualIndex}
              className={`bet-card ${option?.type || 'positive'} ${hasBet ? 'has-bet' : ''} ${isWinner ? 'winner' : ''} ${isLoser ? 'loser' : ''} ${isClaimed ? 'claimed' : ''}`}
              title={option?.tooltip || ''}
            >
              <div className="option-main">{option?.main || `${actualIndex}`}</div>
              <div className="option-sub">{option?.sub || ''}</div>
              {hasBet && <div className="bet-amount">${formatUSDC(stake)}</div>}
              {isClaimed && <div className="claimed-badge">CLAIMED</div>}
            </div>
          );
        })}
      </div>

      {canClaim && (
        <button
          className="claim-btn"
          onClick={handleClaimClick}
          disabled={claiming === pos.id}
        >
          {claiming === pos.id ? 'Claiming...' : pos.voided ? 'Claim Refund' : 'Claim Winnings'}
        </button>
      )}
    </div>
  );
}

export default MyBets;
